import type { TWifiFailReason, TWifiStatus } from '@/helpers/androidWifiProvisioning'
import { computed, ref } from 'vue'
import { useKioskServiceUnlock } from '@/composables/useKioskServiceUnlock'
import {
  clearSavedWifi,
  configureWifi,
  fetchWifiStatus,
  validateWifiPsk,
  validateWifiSsid
} from '@/helpers/androidWifiProvisioning'
import { wifiText } from '@/helpers/wifiCopy'
import {
  initialWifiSetupState,
  wifiSetupReducer
} from '@/helpers/wifiSetupMachine'
import { getPlatformInstance } from '@/platform'

const sheetOpen = ref(false)
const state = ref(initialWifiSetupState())
const statusSnap = ref<TWifiStatus | null>(null)
const unlockLabel = ref('')
const successSeconds = ref(0)
const busy = ref(false)

let servicePin: string | null | undefined
let armedUntil = 0
let opening = false
let tickHandle: ReturnType<typeof setInterval> | null = null
let stepHandle: ReturnType<typeof setInterval> | null = null
let kiosk: ReturnType<typeof useKioskServiceUnlock> | null = null

function kioskApi(): ReturnType<typeof useKioskServiceUnlock> {
  if (kiosk == null) {
    kiosk = useKioskServiceUnlock()
  }
  return kiosk
}

function dispatch(event: Parameters<typeof wifiSetupReducer>[1]): void {
  state.value = wifiSetupReducer(state.value, event)
}

function stopTick(): void {
  if (tickHandle != null) {
    clearInterval(tickHandle)
    tickHandle = null
  }
}

function stopSteps(): void {
  if (stepHandle != null) {
    clearInterval(stepHandle)
    stepHandle = null
  }
}

function closeSheet(): void {
  sheetOpen.value = false
  stopTick()
  stopSteps()
  successSeconds.value = 0
  armedUntil = 0
  state.value = { ...state.value, pskDraft: '', showPsk: false }
}

/**
 * Android Back while the sheet is up. Closes the sheet and leaves the status panel open.
 * Applying has no cancel, so Back is swallowed and the panel stays.
 */
export function consumeWifiSetupBack(): boolean {
  if (!sheetOpen.value) {
    return false
  }
  if (state.value.phase !== 'applying') {
    closeSheet()
  }
  return true
}

/** Status panel went away (auto-hide, triple-tap, remote). The sheet must not stay latched open. */
export function closeWifiSetupSheet(): void {
  closeSheet()
}

function tick(): void {
  const until = kioskApi().unlockedUntilMs.value
  if (until > Date.now()) {
    armedUntil = until
    const total = Math.max(0, Math.ceil((until - Date.now()) / 1000))
    const mm = String(Math.floor(total / 60)).padStart(2, '0')
    const ss = String(total % 60).padStart(2, '0')
    unlockLabel.value = `${mm}:${ss}`
  }
  else {
    unlockLabel.value = ''
    if (sheetOpen.value && armedUntil > 0) {
      closeSheet()
      return
    }
  }
  if (sheetOpen.value && state.value.phase === 'success' && successSeconds.value > 0) {
    successSeconds.value -= 1
    if (successSeconds.value <= 0) {
      closeSheet()
    }
  }
}

function ensureTick(): void {
  if (tickHandle != null) {
    return
  }
  tick()
  tickHandle = setInterval(tick, 1000)
}

export function isWifiSetupSheetOpen(): boolean {
  return sheetOpen.value
}

export function useWifiProvisioning() {
  kioskApi()
  const unlockLine = computed(() =>
    unlockLabel.value ? wifiText('sheet.unlocked', { 'mm:ss': unlockLabel.value }) : ''
  )

  async function openWifiSetup(pin: string | null | undefined): Promise<void> {
    if (opening || sheetOpen.value) {
      return
    }
    opening = true
    try {
      servicePin = pin
      const granted = await kioskApi().ensureServiceAccess(pin, {
        title: wifiText('pin.title'),
        dropLockTask: false
      })
      if (!granted) {
        return
      }
      sheetOpen.value = true
      armedUntil = kioskApi().unlockedUntilMs.value
      ensureTick()
      const status = await fetchWifiStatus()
      statusSnap.value = status
      if (status == null) {
        dispatch({ type: 'unsupported' })
        return
      }
      if (!status.deviceOwner) {
        dispatch({ type: 'notOwner' })
        return
      }
      dispatch({ type: 'showStatus', savedSsid: status.saved?.ssid ?? null })
    }
    finally {
      opening = false
    }
  }

  function beginEdit(): void {
    dispatch({ type: 'editFresh' })
  }

  function backToEdit(): void {
    dispatch({ type: 'editKeepSsid' })
  }

  function setSsid(value: string): void {
    dispatch({ type: 'ssid', value })
  }

  function setPsk(value: string): void {
    dispatch({ type: 'psk', value })
  }

  function toggleShowPsk(): void {
    dispatch({ type: 'toggleShow' })
  }

  async function submit(): Promise<void> {
    if (busy.value || state.value.phase !== 'edit') {
      return
    }
    const ssid = state.value.ssidDraft.trim()
    dispatch({ type: 'ssid', value: ssid })
    if (validateWifiSsid(ssid) || validateWifiPsk(state.value.pskDraft)) {
      dispatch({ type: 'showFieldErrors' })
      return
    }
    const passphrase = state.value.pskDraft
    busy.value = true
    dispatch({ type: 'apply' })
    let step = 0
    stopSteps()
    stepHandle = setInterval(() => {
      step = Math.min(2, step + 1)
      dispatch({ type: 'step', step })
    }, 700)
    let ok = false
    let reason: TWifiFailReason | null = 'timeout'
    try {
      const result = await configureWifi({ ssid, psk: passphrase })
      if (result == null) {
        reason = 'timeout'
      }
      else {
        ok = result.ok
        reason = result.ok ? null : (result.reason ?? 'timeout')
      }
    }
    catch {
      reason = 'timeout'
    }
    finally {
      stopSteps()
      busy.value = false
    }
    if (!sheetOpen.value) {
      return
    }
    dispatch({ type: 'result', ok, reason, ssid })
    if (ok) {
      successSeconds.value = 8
    }
    const next = await fetchWifiStatus()
    if (next) {
      statusSnap.value = next
    }
  }

  function askForget(): void {
    dispatch({ type: 'forgetAsk' })
  }

  function cancelForget(): void {
    dispatch({ type: 'forgetCancel' })
  }

  async function confirmForget(): Promise<void> {
    if (busy.value) {
      return
    }
    busy.value = true
    try {
      await clearSavedWifi()
      dispatch({ type: 'forgot' })
      statusSnap.value = await fetchWifiStatus()
    }
    finally {
      busy.value = false
    }
  }

  async function openAndroidSettings(): Promise<void> {
    const pin = servicePin
    closeSheet()
    if (!await kioskApi().ensureServiceAccess(pin)) {
      return
    }
    await getPlatformInstance().openSystemSettings()
  }

  return {
    sheetOpen,
    state,
    statusSnap,
    unlockLine,
    successSeconds,
    busy,
    openWifiSetup,
    closeSheet,
    beginEdit,
    backToEdit,
    setSsid,
    setPsk,
    toggleShowPsk,
    submit,
    askForget,
    cancelForget,
    confirmForget,
    openAndroidSettings
  }
}
